/**
 * Image metadata and raw pixel access.
 *
 * The core never decodes files itself: browsers hand it an `ImageBitmap` /
 * canvas, the optional Python fallback hands it a decoded matrix. Core only
 * sees a plain, transferable buffer so it can also run inside a Web Worker.
 */

export type SupportedMimeType =
  | 'image/jpeg'
  | 'image/png'
  | 'image/webp'
  | 'image/heic'
  | 'image/heif'
  | 'image/bmp'
  | 'application/octet-stream';

export interface ImageMeta {
  /** Pixel width of the decoded image. */
  width: number;
  /** Pixel height of the decoded image. */
  height: number;
  /** Decoded MIME type, sniffed from magic bytes — never trusted from `Content-Type`. */
  mimeType?: SupportedMimeType | string;
  /** File size in bytes when the source was an uploaded file. */
  sizeBytes?: number;
  /** Original file name, only used for display and never sent anywhere by default. */
  fileName?: string;
  /** EXIF orientation as reported by the decoder (1..8), if known. */
  exifOrientation?: number;
  /** Where the pixels came from; useful for the report and for tests. */
  source?: 'file' | 'camera' | 'url' | 'synthetic';
}

/**
 * A planar pixel buffer. `channels` is 1 (luma), 3 (RGB) or 4 (RGBA) and the
 * data layout is row-major. Buffers are usually a *downscaled* copy produced by
 * `downscaleForAnalysis`, which keeps background/quality analysis under a few
 * milliseconds on a phone.
 */
export interface PixelBuffer {
  data: Uint8ClampedArray;
  width: number;
  height: number;
  channels: 1 | 3 | 4;
}

export interface AnalysisBuffer extends PixelBuffer {
  /** How much the buffer was scaled relative to the original image. */
  scale: number;
  /** Luma plane, always channels === 1, kept separately for cheap metrics. */
  luma: Uint8ClampedArray;
}

export const createLumaPlane = (buffer: PixelBuffer): Uint8ClampedArray => {
  const { data, width, height, channels } = buffer;
  const luma = new Uint8ClampedArray(width * height);
  for (let i = 0, p = 0; i < width * height; i += 1, p += channels) {
    if (channels === 1) {
      luma[i] = data[p] as number;
      continue;
    }
    const r = data[p] as number;
    const g = data[p + 1] as number;
    const b = data[p + 2] as number;
    // ITU-R BT.601 luma, adequate for exposure/sharpness heuristics.
    luma[i] = (0.299 * r + 0.587 * g + 0.114 * b) | 0;
  }
  return luma;
};

export const isPixelBuffer = (value: unknown): value is PixelBuffer => {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<PixelBuffer>;
  return (
    candidate.data instanceof Uint8ClampedArray &&
    typeof candidate.width === 'number' &&
    typeof candidate.height === 'number' &&
    (candidate.channels === 1 || candidate.channels === 3 || candidate.channels === 4)
  );
};

/** Aspect ratio as width / height. */
export const aspectRatio = (width: number, height: number): number =>
  height === 0 ? 0 : width / height;

/** Total pixel count, used for decompression-bomb guards. */
export const pixelCount = (image: { width: number; height: number }): number =>
  image.width * image.height;
